import { FIXED_LIMITS, type ChannelDto } from '@lagerfeuer/shared';
import { useQueryClient } from '@tanstack/react-query';
import { Paperclip, Smile, X } from 'lucide-react';
import { useId, useRef, useState } from 'react';
import { EmojiPickerPopover } from '../../components/EmojiPicker';
import { Modal } from '../../components/Modal';
import { anchorFrom, type Anchor } from '../../components/Popover';
import { emojiToString } from '../../lib/emojiString';
import { errorMessage } from '../../lib/api';
import { messagesApi, threadsApi } from '../../lib/endpoints';
import { uploadDraftFiles } from '../messages/send';
import { useAppStore } from '../../store/app';
import { can, useChannelPermissions } from '../../store/selectors';
import { TagPicker } from './TagPicker';
import './forum.css';

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(2)} GB`;
}

/**
 * „Neuer Beitrag" in einem Forum (Vertrag 3.4: POST /channels/:id/threads mit `message`).
 * Anhänge werden vorher in den Forenkanal hochgeladen (3.6) und mit `attachmentIds` übergeben.
 */
export function NewPostDialog({ forum, onClose, onCreated }: { forum: ChannelDto; onClose: () => void; onCreated: (thread: ChannelDto) => void }) {
  const queryClient = useQueryClient();
  const limits = useAppStore((s) => s.limits);
  const perms = useChannelPermissions(forum.id);
  const isMod = can(perms, 'MANAGE_THREADS');
  const canAttach = can(perms, 'ATTACH_FILES');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [progress, setProgress] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const contentRef = useRef<HTMLTextAreaElement>(null);
  const [emojiAnchor, setEmojiAnchor] = useState<Anchor | null>(null);
  const id = useId();

  /** Emoji an der Cursorposition einfügen (eigene als `<:name:id>`). */
  const insertEmoji = (text: string) => {
    const el = contentRef.current;
    const start = el?.selectionStart ?? content.length;
    const end = el?.selectionEnd ?? content.length;
    const next = content.slice(0, start) + text + content.slice(end);
    setContent(next);
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(start + text.length, start + text.length);
    });
  };
  const maxFiles = limits.maxAttachmentsPerMessage || Infinity;
  const dirty = !!(title || content || files.length);
  const valid = title.trim().length > 0 && (content.trim().length > 0 || files.length > 0) && content.length <= limits.maxMessageLength;

  const addFiles = (list: FileList | null) => {
    if (!list) return;
    const next = [...files];
    for (const f of Array.from(list)) {
      if (f.size > limits.maxUploadBytes) {
        setError(`„${f.name}" ist zu groß (höchstens ${formatBytes(limits.maxUploadBytes)}).`);
        continue;
      }
      if (next.length >= maxFiles) {
        setError(`Höchstens ${maxFiles} Anhänge je Beitrag.`);
        break;
      }
      next.push(f);
    }
    setFiles(next);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    let attachmentIds: string[] = [];
    try {
      if (files.length) {
        setProgress(0);
        // Einzeln hochladen wie im Composer (D21): sonst scheitern mehrere große Dateien am Body-Limit.
        attachmentIds = await uploadDraftFiles(
          forum.id,
          files.map((file, i) => ({ id: String(i), file, spoiler: false })),
          { onProgress: (loaded, total) => setProgress(total ? Math.round((loaded / total) * 100) : 0) },
        );
      }
      const created = await threadsApi.create(forum.id, {
        name: title.trim(),
        appliedTagIds: tags.length ? tags : undefined,
        message: { content: content.trim(), ...(attachmentIds.length ? { attachmentIds } : {}) },
      });
      const { firstMessage, ...dto } = created;
      useAppStore.getState().dispatch('THREAD_CREATE', dto);
      if (firstMessage) queryClient.setQueryData(['forum-starter', dto.id], firstMessage);
      void queryClient.invalidateQueries({ queryKey: ['threads', forum.id] });
      onCreated(dto);
    } catch (err) {
      // Hochgeladene, nicht verwendete Anhänge wieder freigeben (sonst räumt erst orphanUploadHours auf).
      for (const a of attachmentIds) void messagesApi.deleteAttachment(a).catch(() => undefined);
      setError(errorMessage(err));
      setProgress(null);
      setBusy(false);
    }
  };

  return (
    <Modal title="Neuer Beitrag" onClose={onClose} wide sticky={dirty} testId="forum-new-post-dialog">
      <form className="stack" onSubmit={(e) => void submit(e)} data-testid="forum-new-post-form">
        <div className="field">
          <label htmlFor={`${id}-title`}>Titel</label>
          <input
            id={`${id}-title`}
            className="input"
            value={title}
            maxLength={FIXED_LIMITS.threadTitleMax}
            required
            autoFocus
            onChange={(e) => setTitle(e.target.value)}
            data-testid="forum-post-title-input"
          />
        </div>
        {(forum.availableTags?.length ?? 0) > 0 && (
          <div className="field">
            <span className="label" id={`${id}-tags`}>
              Tags
            </span>
            <TagPicker tags={forum.availableTags ?? []} selected={tags} onChange={setTags} isMod={isMod} />
          </div>
        )}
        <div className="field">
          <label htmlFor={`${id}-content`}>Inhalt</label>
          <textarea
            id={`${id}-content`}
            className="textarea"
            rows={6}
            ref={contentRef}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            aria-describedby={`${id}-count`}
            placeholder="Beschreibe dein Anliegen – Markdown wird unterstützt."
            data-testid="forum-post-content"
          />
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <button
              type="button"
              className="icon-btn"
              aria-label="Emoji einfügen"
              aria-haspopup="dialog"
              onClick={(e) => setEmojiAnchor(anchorFrom(e.currentTarget))}
              data-testid="forum-post-emoji"
            >
              <Smile size={18} />
            </button>
            <span id={`${id}-count`} className="field-hint" aria-live="polite">
              {content.length} / {limits.maxMessageLength}
            </span>
          </div>
          {emojiAnchor && (
            <EmojiPickerPopover
              anchor={emojiAnchor}
              onClose={() => setEmojiAnchor(null)}
              serverId={forum.serverId}
              allowExternal={can(perms, 'USE_EXTERNAL_EMOJIS')}
              onSelect={(e) => insertEmoji(emojiToString(e))}
            />
          )}
        </div>
        {canAttach && (
          <div className="field">
            <input ref={fileRef} type="file" multiple hidden onChange={(e) => {
                addFiles(e.target.files);
                e.target.value = '';
              }} data-testid="forum-post-file-input" />
            <div className="row">
              <button type="button" className="btn btn-sm" onClick={() => fileRef.current?.click()} disabled={busy || files.length >= maxFiles} data-testid="forum-post-attach">
                <Paperclip size={14} aria-hidden="true" /> Datei anhängen
              </button>
            </div>
            {files.length > 0 && (
              <ul className="forum-files" role="list" aria-label="Anhänge">
                {files.map((f, i) => (
                  <li key={`${f.name}-${i}`} className="forum-file" data-testid="forum-post-file">
                    <Paperclip size={14} aria-hidden="true" />
                    <span className="truncate">{f.name}</span>
                    <span className="small faint">{formatBytes(f.size)}</span>
                    <button
                      type="button"
                      className="icon-btn icon-btn-xs"
                      disabled={busy}
                      onClick={() => setFiles(files.filter((_, j) => j !== i))}
                      aria-label={`${f.name} entfernen`}
                      data-testid="forum-post-file-remove"
                    >
                      <X size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {progress !== null && (
              <div className="progress" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100} aria-label="Hochladen" data-testid="forum-post-progress">
                <span style={{ width: `${progress}%` }} />
              </div>
            )}
          </div>
        )}
        {error && (
          <p className="error-text small" role="alert" data-testid="forum-post-error">
            {error}
          </p>
        )}
        <div className="row forum-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose} data-testid="forum-post-cancel">
            Abbrechen
          </button>
          <button type="submit" className="btn btn-primary" disabled={!valid || busy} data-testid="forum-post-submit">
            {busy ? 'Wird veröffentlicht …' : 'Veröffentlichen'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
